/** Signing in, and activating a new device. */
const auth = {
  en: {
    login: {
      title: "Shop sign in",
      subtitle: "Sign in to manage your store",
      identifier: "Email or phone",
      password: "Password",
      submit: "Sign in",
      submitting: "Signing in…",
      failed: "Login failed",
      platformAccount:
        "This is a platform account. Sign in at the platform admin site instead.",
    },
    forgot: {
      link: "Forgot your password?",
      title: "Reset your password",
      intro: "Enter your account's email address and we will send you a link to choose a new password.",
      email: "Email address",
      submit: "Send the link",
      sending: "Sending…",
      sent: "If that address has an account, a link is on its way. It works once and expires in one hour.",
      back: "Back to sign in",
    },
    reset: {
      title: "Choose a new password",
      password: "New password",
      confirm: "Repeat the new password",
      hint: "At least 8 characters.",
      submit: "Save the new password",
      saving: "Saving…",
      mismatch: "The two passwords are not the same.",
      done: "Your password has been changed. You can sign in now.",
      invalid: "This link is invalid or has expired. Ask for a new one.",
      missing: "This link is incomplete. Ask for a new one.",
      signIn: "Go to sign in",
    },
    verify: {
      title: "Email address",
      working: "Confirming your email address…",
      done: "Your email address is confirmed. Thank you!",
      invalid: "This link is invalid or has expired. Sign in and ask for a new one.",
      continue: "Continue",
      bannerText: "Please confirm your email address. We sent you a link.",
      resend: "Send the link again",
      resending: "Sending…",
      resent: "A new link is on its way.",
      resendFailed: "Could not send it right now. Try again in a little while.",
      dismiss: "Hide",
    },
    credentials: {
      title: "Protect your account",
      passwordIntro: "You are using a password that was given to you. Choose your own before you continue.",
      current: "Current password",
      newPassword: "New password",
      confirm: "Repeat the new password",
      hint: "At least 8 characters, and not the one you were given.",
      save: "Save my password",
      saving: "Saving…",
      mismatch: "The two passwords are not the same.",
      wrongCurrent: "The current password is not correct.",
      failed: "Could not change the password. Try another one.",
      signOut: "Sign out",
      pinTitle: "Choose your till PIN",
      pinIntro: "Your till PIN is how you open the register. Choose four digits that only you know.",
      pinConfirm: "Enter the same PIN again to confirm.",
      pinMismatch: "The two PINs were not the same. Start again.",
      pinFailed: "Could not save this PIN. Try another one.",
      pinSaving: "Saving…",
    },
    legal: {
      termsLink: "Terms of service",
      privacyLink: "Privacy policy",
      back: "Back",
    },
    activate: {
      title: "Set up this device",
      signinIntro:
        "Sign in with your shop account to add this device to your shop.",
      nameIntro:
        "Give this device a name you'll recognise in your device list.",
      notRecognised: "Those details were not recognised.",
      failed: "Could not activate this device.",
      allInUse: "All {limit} devices are in use",
      releaseHint:
        "Release one from Manage store → Devices on a device you still have, or contact support to add another.",
      continue: "Continue",
      signingIn: "Signing in…",
      deviceName: "Device name",
      deviceNameHint: "For example “counter tablet” or “owner phone”.",
      activate: "Activate this device",
      activating: "Activating…",
      labels: {
        tablet: "Counter tablet",
        phone: "Phone",
        laptop: "Shop laptop",
      },
    },
  },

  fr: {
    login: {
      title: "Connexion boutique",
      subtitle: "Connectez-vous pour gérer votre boutique",
      identifier: "E-mail ou téléphone",
      password: "Mot de passe",
      submit: "Se connecter",
      submitting: "Connexion…",
      failed: "Échec de la connexion",
      platformAccount:
        "Ce compte est un compte de plateforme. Connectez-vous plutôt sur le site d'administration de la plateforme.",
    },
    forgot: {
      link: "Mot de passe oublié ?",
      title: "Réinitialiser votre mot de passe",
      intro: "Saisissez l'adresse e-mail de votre compte et nous vous enverrons un lien pour choisir un nouveau mot de passe.",
      email: "Adresse e-mail",
      submit: "Envoyer le lien",
      sending: "Envoi…",
      sent: "Si cette adresse a un compte, un lien vous a été envoyé. Il ne fonctionne qu'une fois et expire dans une heure.",
      back: "Retour à la connexion",
    },
    reset: {
      title: "Choisir un nouveau mot de passe",
      password: "Nouveau mot de passe",
      confirm: "Répétez le nouveau mot de passe",
      hint: "Au moins 8 caractères.",
      submit: "Enregistrer le nouveau mot de passe",
      saving: "Enregistrement…",
      mismatch: "Les deux mots de passe ne sont pas identiques.",
      done: "Votre mot de passe a été modifié. Vous pouvez vous connecter.",
      invalid: "Ce lien est invalide ou a expiré. Demandez-en un nouveau.",
      missing: "Ce lien est incomplet. Demandez-en un nouveau.",
      signIn: "Aller à la connexion",
    },
    verify: {
      title: "Adresse e-mail",
      working: "Confirmation de votre adresse e-mail…",
      done: "Votre adresse e-mail est confirmée. Merci !",
      invalid: "Ce lien est invalide ou a expiré. Connectez-vous et demandez-en un nouveau.",
      continue: "Continuer",
      bannerText: "Merci de confirmer votre adresse e-mail. Nous vous avons envoyé un lien.",
      resend: "Renvoyer le lien",
      resending: "Envoi…",
      resent: "Un nouveau lien vous a été envoyé.",
      resendFailed: "Envoi impossible pour le moment. Réessayez dans un moment.",
      dismiss: "Masquer",
    },
    credentials: {
      title: "Protégez votre compte",
      passwordIntro: "Vous utilisez un mot de passe qui vous a été donné. Choisissez le vôtre avant de continuer.",
      current: "Mot de passe actuel",
      newPassword: "Nouveau mot de passe",
      confirm: "Répétez le nouveau mot de passe",
      hint: "Au moins 8 caractères, et différent de celui qu'on vous a donné.",
      save: "Enregistrer mon mot de passe",
      saving: "Enregistrement…",
      mismatch: "Les deux mots de passe ne sont pas identiques.",
      wrongCurrent: "Le mot de passe actuel est incorrect.",
      failed: "Impossible de changer le mot de passe. Essayez-en un autre.",
      signOut: "Se déconnecter",
      pinTitle: "Choisissez votre code PIN de caisse",
      pinIntro: "Votre code PIN de caisse sert à ouvrir la caisse. Choisissez quatre chiffres que vous seul connaissez.",
      pinConfirm: "Saisissez le même code PIN une seconde fois pour confirmer.",
      pinMismatch: "Les deux codes PIN ne sont pas identiques. Recommencez.",
      pinFailed: "Impossible d'enregistrer ce code PIN. Essayez-en un autre.",
      pinSaving: "Enregistrement…",
    },
    legal: {
      termsLink: "Conditions d'utilisation",
      privacyLink: "Politique de confidentialité",
      back: "Retour",
    },
    activate: {
      title: "Configurer cet appareil",
      signinIntro:
        "Connectez-vous avec le compte de votre boutique pour y ajouter cet appareil.",
      nameIntro:
        "Donnez à cet appareil un nom que vous reconnaîtrez dans la liste des appareils.",
      notRecognised: "Ces identifiants n'ont pas été reconnus.",
      failed: "Impossible d'activer cet appareil.",
      allInUse: "Les {limit} appareils sont tous utilisés",
      releaseHint:
        "Libérez-en un depuis Gérer la boutique → Appareils sur un appareil que vous avez encore, ou contactez le support pour en ajouter un.",
      continue: "Continuer",
      signingIn: "Connexion…",
      deviceName: "Nom de l'appareil",
      deviceNameHint:
        "Par exemple « tablette du comptoir » ou « téléphone du gérant ».",
      activate: "Activer cet appareil",
      activating: "Activation…",
      labels: {
        tablet: "Tablette du comptoir",
        phone: "Téléphone",
        laptop: "Ordinateur de la boutique",
      },
    },
  },

  ar: {
    login: {
      title: "تسجيل دخول المتجر",
      subtitle: "سجّل الدخول لإدارة متجرك",
      identifier: "البريد الإلكتروني أو الهاتف",
      password: "كلمة المرور",
      submit: "تسجيل الدخول",
      submitting: "جارٍ تسجيل الدخول…",
      failed: "فشل تسجيل الدخول",
      platformAccount:
        "هذا حساب خاص بالمنصة. سجّل الدخول من موقع إدارة المنصة بدلاً من ذلك.",
    },
    forgot: {
      link: "نسيت كلمة المرور؟",
      title: "إعادة تعيين كلمة المرور",
      intro: "أدخل البريد الإلكتروني لحسابك وسنرسل إليك رابطًا لاختيار كلمة مرور جديدة.",
      email: "البريد الإلكتروني",
      submit: "إرسال الرابط",
      sending: "جارٍ الإرسال…",
      sent: "إذا كان لهذا العنوان حساب، فقد أُرسل إليه رابط. يعمل مرة واحدة وتنتهي صلاحيته خلال ساعة.",
      back: "العودة إلى تسجيل الدخول",
    },
    reset: {
      title: "اختيار كلمة مرور جديدة",
      password: "كلمة المرور الجديدة",
      confirm: "أعد كتابة كلمة المرور الجديدة",
      hint: "8 أحرف على الأقل.",
      submit: "حفظ كلمة المرور الجديدة",
      saving: "جارٍ الحفظ…",
      mismatch: "كلمتا المرور غير متطابقتين.",
      done: "تم تغيير كلمة المرور. يمكنك تسجيل الدخول الآن.",
      invalid: "هذا الرابط غير صالح أو انتهت صلاحيته. اطلب رابطًا جديدًا.",
      missing: "هذا الرابط ناقص. اطلب رابطًا جديدًا.",
      signIn: "الذهاب إلى تسجيل الدخول",
    },
    verify: {
      title: "البريد الإلكتروني",
      working: "جارٍ تأكيد بريدك الإلكتروني…",
      done: "تم تأكيد بريدك الإلكتروني. شكرًا لك!",
      invalid: "هذا الرابط غير صالح أو انتهت صلاحيته. سجّل الدخول واطلب رابطًا جديدًا.",
      continue: "متابعة",
      bannerText: "يُرجى تأكيد بريدك الإلكتروني. أرسلنا إليك رابطًا.",
      resend: "إعادة إرسال الرابط",
      resending: "جارٍ الإرسال…",
      resent: "أُرسل إليك رابط جديد.",
      resendFailed: "تعذّر الإرسال الآن. حاول مرة أخرى بعد قليل.",
      dismiss: "إخفاء",
    },
    credentials: {
      title: "احمِ حسابك",
      passwordIntro: "كلمة المرور التي تستخدمها أُعطيت لك. اختر كلمة مرور خاصة بك قبل المتابعة.",
      current: "كلمة المرور الحالية",
      newPassword: "كلمة المرور الجديدة",
      confirm: "أعد كتابة كلمة المرور الجديدة",
      hint: "8 أحرف على الأقل، ويجب ألا تكون الكلمة التي أُعطيت لك.",
      save: "حفظ كلمة مروري",
      saving: "جارٍ الحفظ…",
      mismatch: "كلمتا المرور غير متطابقتين.",
      wrongCurrent: "كلمة المرور الحالية غير صحيحة.",
      failed: "تعذّر تغيير كلمة المرور. جرّب كلمة أخرى.",
      signOut: "تسجيل الخروج",
      pinTitle: "اختر رمز PIN الخاص بالصندوق",
      pinIntro: "رمز PIN الخاص بالصندوق هو ما تفتح به الصندوق. اختر أربعة أرقام لا يعرفها غيرك.",
      pinConfirm: "أدخل الرمز نفسه مرة أخرى للتأكيد.",
      pinMismatch: "الرمزان غير متطابقين. ابدأ من جديد.",
      pinFailed: "تعذّر حفظ هذا الرمز. جرّب رمزًا آخر.",
      pinSaving: "جارٍ الحفظ…",
    },
    legal: {
      termsLink: "شروط الاستخدام",
      privacyLink: "سياسة الخصوصية",
      back: "رجوع",
    },
    activate: {
      title: "إعداد هذا الجهاز",
      signinIntro: "سجّل الدخول بحساب متجرك لإضافة هذا الجهاز إليه.",
      nameIntro: "امنح هذا الجهاز اسماً تتعرّف عليه في قائمة الأجهزة.",
      notRecognised: "لم يتم التعرّف على هذه البيانات.",
      failed: "تعذّر تفعيل هذا الجهاز.",
      allInUse: "جميع الأجهزة ({limit}) مستخدمة",
      releaseHint:
        "حرّر أحدها من إدارة المتجر ← الأجهزة على جهاز لا يزال بحوزتك، أو تواصل مع الدعم لإضافة جهاز آخر.",
      continue: "متابعة",
      signingIn: "جارٍ تسجيل الدخول…",
      deviceName: "اسم الجهاز",
      deviceNameHint: "مثلاً «جهاز الصندوق» أو «هاتف المالك».",
      activate: "تفعيل هذا الجهاز",
      activating: "جارٍ التفعيل…",
      labels: {
        tablet: "جهاز لوحي للصندوق",
        phone: "هاتف",
        laptop: "حاسوب المتجر",
      },
    },
  },
};

export default auth;
